/* ----------------------------------------------------------------------------
 * VoiceCopilot — the Aura voice popup surface (owner reference redesign 2026-07-18).
 *
 * Dark card (#16141f, rounded 16px). Header = the isolated <AuraMark/> slot +
 * "Aura" + "Your executive assistant · en-GB · <State>" with a Mute toggle and
 * a ✕ close (when onClose is provided). A dark transcript/prompt field, a big
 * PUSH-TO-TALK button (hold to record, release to send) with a Stop (barge-in)
 * beside it, helper text, and a "Type instead… / Send" fallback row.
 *
 * State visuals (2026-07-19): the push-to-talk pill is state-driven — idle keeps
 * the lavender "Hold to talk"; listening/thinking/speaking recolour the pill
 * (teal / violet / amber border+glow+label) and flank the label with a
 * <ControlStateAdornment/> (teal waveform / violet dots / amber waveform). The
 * hold-to-talk, Stop, Mute and barge-in behaviour are UNCHANGED — only the skin
 * + label follow `status`. The current status is reported up via onStatusChange
 * so the dashboard hero can paint its matching audio-wave behind the text.
 *
 * IMPORT-SAFETY (owner directive 2026-07-19 / app-wide move 2026-07-24): NONE of
 * the "Hey Aura" wake code is imported here — statically OR lazily. The wake
 * ENGINE now lives app-wide in <HandsFreeProvider> at the shell (arms ONCE,
 * survives route changes). VoiceCopilot imports ONLY the pure `WakeVisualState`
 * TYPE from handsFreeConfig and PAINTS the wake strip from the plain state passed
 * down via props (handsFreeActive + wakeState). The main bundle every user loads
 * stays 100% wake-free — no wake code enters this file's graph at all.
 *
 * Playback fix (2026-07-18): the spoken reply is bound to REAL audio start via
 * useVoiceController (AudioContext resumed on the hold gesture); if the browser
 * blocks autoplay a visible "tap to play" control is surfaced — never a silent
 * stuck "speaking". Mute suppresses TTS; Stop is barge-in. NO external action
 * happens without an explicit APPROVE. No provider key is ever client-side.
 * -------------------------------------------------------------------------- */

import { useEffect, useState, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { cn } from '@/lib/utils';
import { useVoiceController, type UseVoiceControllerOptions, type VoiceController } from './useVoiceController.ts';
import { AuraMark } from './AuraMark.tsx';
import type { VoiceStatus } from './types.ts';
import { STATE_ACCENTS } from './state-visuals/stateVisualStyle.ts';
import { ControlStateAdornment } from './state-visuals/ControlStateAdornment.tsx';
import type { WakeVisualState } from './wake/handsFreeConfig.ts';

// NOTE (owner directive 2026-07-24): the "Hey Aura" WAKE ENGINE no longer mounts
// here — it lives app-wide in <HandsFreeProvider> at the shell so it arms ONCE
// and survives every route change. VoiceCopilot only PAINTS the wake strip from
// a plain WakeVisualState passed down (handsFreeActive + wakeState props), so
// the main bundle stays 100% wake-free (no static OR lazy wake import here).

const STATE_LABEL: Record<VoiceStatus, string> = {
  idle: 'Ready',
  listening: 'Listening',
  thinking: 'Thinking',
  speaking: 'Speaking',
  error: 'Something went wrong',
};

const LAVENDER = '#e7e4ff';
const CARD_BG = '#16141f';
const FIELD_BG = '#0f0e16';

function MicIcon({ className }: { className?: string }) {
  return (
    <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M12 15a3.5 3.5 0 0 0 3.5-3.5v-5a3.5 3.5 0 1 0-7 0v5A3.5 3.5 0 0 0 12 15Z" fill="currentColor" />
      <path d="M5.5 11.5a6.5 6.5 0 0 0 13 0M12 18v3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" fill="none" />
    </svg>
  );
}

function BrainIcon({ className }: { className?: string }) {
  return (
    <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M9 4.5A2.5 2.5 0 0 0 6.5 7 2.5 2.5 0 0 0 5 9.3a2.5 2.5 0 0 0 .4 4.2A2.6 2.6 0 0 0 8 17a2.2 2.2 0 0 0 4 .3M15 4.5A2.5 2.5 0 0 1 17.5 7 2.5 2.5 0 0 1 19 9.3a2.5 2.5 0 0 1-.4 4.2A2.6 2.6 0 0 1 16 17a2.2 2.2 0 0 1-4 .3V4.6"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

function ReplayIcon({ className }: { className?: string }) {
  return (
    <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M3 12a9 9 0 1 0 2.64-6.36L3 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <path d="M3 3v5h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  );
}

export interface VoiceCopilotProps extends UseVoiceControllerOptions {
  /** show the local-demo prompt box (mock STT only). Default false. */
  demo?: boolean;
  /** when provided, renders the ✕ close control in the header. */
  onClose?: () => void;
  /** reports the live voice status up (lets the hero paint its matching wave). */
  onStatusChange?: (status: VoiceStatus) => void;
  className?: string;
}

export function VoiceCopilot({ demo = false, onClose, onStatusChange, className, ...controllerOptions }: VoiceCopilotProps) {
  const v = useVoiceController({ ...controllerOptions, prewarmMic: controllerOptions.prewarmMic ?? !demo });
  // Standalone popup (dashboard / rail / studio manual "Talk to Aura"). App-wide
  // "Hey Aura" wake + its strip are driven by the shell's HandsFreeProvider, not
  // here, so no wakeState is passed in this path.
  return <VoiceCopilotView v={v} demo={demo} onClose={onClose} onStatusChange={onStatusChange} className={className} />;
}

export interface VoiceCopilotViewProps {
  /** the voice controller to render (a standalone one, or the shell's app-wide
   *  instance owned by HandsFreeProvider). */
  v: VoiceController;
  demo?: boolean;
  onClose?: () => void;
  onStatusChange?: (status: VoiceStatus) => void;
  className?: string;
  /** #8: one-tap device mic kill rendered in the wake strip (popup only). */
  onTurnOffMic?: () => void;
  /** app-wide "Hey Aura" is active (mic mode continuous/both) — paint the strip. */
  handsFreeActive?: boolean;
  /** the plain wake visual state from the shell provider (drives the strip). */
  wakeState?: WakeVisualState | null;
}

/** The presentational Aura popup, driven by an EXTERNAL controller. Split out so
 *  the shell's app-wide wake mount (HandsFreeProvider) renders the SAME popup
 *  bound to its persistent controller on a wake, while the three standalone
 *  surfaces keep creating their own controller via <VoiceCopilot/>. */
export function VoiceCopilotView({ v, demo = false, onClose, onStatusChange, className, handsFreeActive = false, wakeState = null, onTurnOffMic }: VoiceCopilotViewProps) {
  const [textDraft, setTextDraft] = useState('');
  const [demoDraft, setDemoDraft] = useState('');
  const [replaying, setReplaying] = useState(false);

  const { state, persona } = v;
  const status = state.status;
  const liveTranscript = state.interimTranscript || state.finalTranscript;
  const holdActive = status === 'listening';
  const stateLabel = holdActive && state.finalizing ? 'Sending' : STATE_LABEL[status];

  // Report the live status up (hero wave mirrors it). Reset happens on unmount.
  useEffect(() => {
    onStatusChange?.(status);
  }, [status, onStatusChange]);
  useEffect(() => {
    return () => onStatusChange?.('idle');
  }, [onStatusChange]);

  // State-driven skin for the push-to-talk pill. idle => the lavender rest look.
  const accent = STATE_ACCENTS[status];
  const controlStyle =
    status === 'idle' || status === 'error'
      ? { background: LAVENDER, color: '#1a1726' }
      : { background: accent.bg, color: accent.text, border: `1.5px solid ${accent.border}`, boxShadow: `0 0 18px -1px ${accent.glow}` };
  const controlLabel =
    status === 'listening'
      ? state.finalizing ? 'Sending…' : 'Listening…'
      : status === 'thinking'
        ? 'Thinking…'
        : status === 'speaking'
          ? 'Speaking…'
          : 'Hold to talk';

  // Push-to-talk edge handlers — POINTER EVENTS (unify mouse + touch + pen into a
  // SINGLE stream). This replaces the old mouse+touch dual binding, which was
  // broken on mobile: React registers `touchstart` as a PASSIVE root listener, so
  // the old `preventDefault()` inside onTouchStart was a NO-OP ("Unable to
  // preventDefault inside passive event listener invocation") — it neither
  // suppressed the emulated/ghost mouse events (double-press / mic re-open) nor
  // page scroll. Pointer events are NOT passive, fire ONCE per interaction (no
  // ghost mouse duplicate), and `setPointerCapture` guarantees we still get
  // pointerup/pointercancel even if the finger/mouse slides off the button
  // (replacing the fragile onMouseLeave end). `touch-action: none` (the
  // `touch-none` class) suppresses scroll/zoom during the hold — so no
  // preventDefault (and no passive-listener warning) is needed. Desktop mouse
  // drives the SAME pointer path (pointerType mouse), so press→hold→release is
  // behaviour-equivalent to before.
  const beginHold = (e: ReactPointerEvent<HTMLButtonElement>) => {
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* pre-pointer-capture browser */ }
    void v.pressToTalk();
  };
  const endHold = (e: ReactPointerEvent<HTMLButtonElement>) => {
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* no-op */ }
    void v.releaseToTalk();
  };
  // Pointer-cancel (system gesture / scroll-steal / call interrupt) ABORTS the
  // hold — discards the capture and sends nothing (was wired to endHold, which
  // SENT a partial turn).
  const cancelHold = (e: ReactPointerEvent<HTMLButtonElement>) => {
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* no-op */ }
    v.cancelToTalk();
  };
  const onHoldKeyDown = (e: ReactKeyboardEvent) => {
    if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) { e.preventDefault(); void v.pressToTalk(); }
  };
  const onHoldKeyUp = (e: ReactKeyboardEvent) => {
    if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); void v.releaseToTalk(); }
  };

  const submitText = async () => {
    const t = textDraft.trim();
    if (t === '') return;
    setTextDraft('');
    await v.submitText(t);
  };

  return (
    <div
      className={cn('flex flex-col gap-3 rounded-2xl border border-white/10 p-4 shadow-2xl sm:p-5', className)}
      style={{ background: CARD_BG }}
      aria-label={`${persona.displayName} voice assistant`}
    >
      {/* Header — isolated AuraMark slot + identity + Mute + close */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <AuraMark state={status} sizePx={36} label={persona.displayName} />
          <div className="leading-tight">
            <div className="text-[15px] font-semibold text-white">{persona.displayName}</div>
            <div className="text-xs text-[#9aa0ad]">
              Your executive assistant · {persona.accent} · {stateLabel}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={state.muted ? v.unmute : v.mute}
            aria-label={state.muted ? 'Unmute Aura' : 'Mute Aura'}
            aria-pressed={state.muted}
            className={cn(
              'rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors duration-fast',
              state.muted
                ? 'border-[#f59e0b]/40 bg-[#f59e0b]/10 text-[#f59e0b]'
                : 'border-white/10 bg-white/5 text-[#c9ccd6] hover:bg-white/10',
            )}
          >
            {state.muted ? 'Unmute' : 'Mute'}
          </button>
          {onClose ? (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close Aura"
              className="rounded-lg p-1.5 text-[#9aa0ad] transition-colors duration-fast hover:bg-white/10 hover:text-white"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </button>
          ) : null}
        </div>
      </div>

      {/* Hands-free wake status — CALM 'listening for Hey Aura' (distinct from the
          teal STT Listening), plus other-tab / error / starting states. Rendered
          PURELY from the plain WakeVisualState the lazy controller reports up — no
          wake code in the main bundle. */}
      {handsFreeActive && wakeState?.optedIn ? (
        <div
          className="flex items-center gap-2 rounded-xl border px-3 py-2 text-xs"
          style={
            wakeState.otherTab || wakeState.error
              ? { borderColor: 'rgba(245,158,11,0.4)', background: 'rgba(245,158,11,0.08)', color: '#f5c66b' }
              : wakeState.armed
                ? { borderColor: 'rgba(139,123,255,0.45)', background: 'rgba(139,123,255,0.10)', color: '#c3c8ff' }
                : { borderColor: 'rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.03)', color: '#9aa0ad' }
          }
          role="status"
          aria-live="polite"
        >
          <span
            aria-hidden
            className={cn('inline-block h-2 w-2 rounded-full', wakeState.armed ? 'animate-pulse' : '')}
            style={{ background: wakeState.otherTab || wakeState.error ? '#f59e0b' : wakeState.armed ? '#8b7bff' : '#6b7280' }}
          />
          <span>
            {wakeState.otherTab
              ? 'Hey Aura is active in another tab'
              : wakeState.error
                ? wakeState.error
                : wakeState.muted
                  ? 'Hey Aura muted — unmute to resume'
                  : wakeState.armed
                    ? 'Listening for “Hey Aura”…'
                    : wakeState.loading
                      ? 'Starting hands-free…'
                      : 'Hey Aura ready'}
          </span>
          {/* #8 (owner 2026-07-25): one-tap DEVICE mic kill. Disarms + releases the
              mic on THIS device only — the account mic-mode preference is never
              touched (revive lives in Settings > Aura Voice). */}
          {onTurnOffMic ? (
            <button
              type="button"
              onClick={onTurnOffMic}
              className="ml-auto shrink-0 rounded-lg border px-2 py-1 text-[11px] font-medium transition-colors hover:bg-white/10"
              style={{ borderColor: 'rgba(255,255,255,0.15)', color: '#e7e9f0' }}
              aria-label="Turn off the microphone on this device"
            >
              Turn off mic
            </button>
          ) : null}
        </div>
      ) : null}

      {/* Transcript / prompt (response) field.
          MOBILE-SCROLL FIX: bound the response/answer area so a long Aura reply
          scrolls WITHIN the panel (first line -> last) instead of growing the
          card past the viewport top, where earlier lines were clipped and
          unreachable (the modal is anchored items-end on mobile). overscroll-
          contain stops scroll-chaining to the modal/page; -webkit-overflow-
          scrolling gives iOS momentum. Desktop (sm+) is UNCHANGED: max-height
          and overflow are reset at the sm breakpoint, so no layout regression. */}
      <div
        className="max-h-[40dvh] min-h-[3.25rem] overflow-y-auto overscroll-contain rounded-xl border border-white/5 px-3.5 py-2.5 text-sm text-[#e7e7ee] sm:max-h-none sm:overflow-visible"
        style={{ background: FIELD_BG, WebkitOverflowScrolling: "touch" }}
        role="log"
        aria-live="polite"
      >
        {liveTranscript ? (
          <p>
            <span className="text-[#9aa0ad]">You:</span> {liveTranscript}
            {status === 'listening' && state.interimTranscript ? <span className="ml-0.5 animate-pulse">▍</span> : null}
          </p>
        ) : (
          <p className="text-[#9aa0ad]">
            Hold to talk, or type below. Try “what’s happening today?”
          </p>
        )}
        {v.auraSections && v.auraSections.length > 0 ? (
          <div className="mt-2 flex flex-col gap-3">
            {v.auraSections.map((sec, i) => (
              <div key={i} className="flex flex-col gap-1">
                {sec.heading ? (
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#a9b0ff]">
                    {sec.heading}
                  </p>
                ) : null}
                {sec.paragraphs.map((para, j) => (
                  <p key={j} className="text-sm leading-relaxed text-[#e7e7ee]">
                    {para}
                  </p>
                ))}
              </div>
            ))}
          </div>
        ) : v.auraSpeech && (status === 'speaking' || status === 'idle') ? (
          <p className="mt-2">
            <span className="text-[#a9b0ff]">{persona.displayName}:</span> {v.auraSpeech}
          </p>
        ) : null}
        {status === 'error' && state.error ? (
          <p className="mt-2 text-red-400">{state.error.message}</p>
        ) : null}
        {v.needsPlaybackTap ? (
          <button
            type="button"
            onClick={() => void v.replay()}
            className="mt-2 inline-flex items-center gap-2 rounded-lg border border-[#a9b0ff]/40 bg-[#a9b0ff]/10 px-2.5 py-1 text-xs font-semibold text-[#c3c8ff] transition-colors duration-fast hover:bg-[#a9b0ff]/20"
            aria-label="Play Aura's reply"
          >
            ▶ Tap to hear Aura
          </button>
        ) : null}
      </div>

      {/* Primary control — PUSH-TO-TALK (state-skinned) + Stop (barge-in) */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          className={cn(
            'flex flex-1 touch-none select-none items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition-transform duration-fast active:scale-[0.99]',
          )}
          style={controlStyle}
          disabled={status === 'thinking'}
          onPointerDown={beginHold}
          onPointerUp={endHold}
          onPointerCancel={cancelHold}
          onKeyDown={onHoldKeyDown}
          onKeyUp={onHoldKeyUp}
          onContextMenu={(e) => e.preventDefault()}
          aria-pressed={holdActive}
          aria-label={holdActive ? 'Recording — release to send' : 'Hold to talk to Aura'}
        >
          <ControlStateAdornment status={status} className="inline-flex items-center" />
          {status === 'thinking' ? <BrainIcon /> : <MicIcon />}
          {controlLabel}
          <ControlStateAdornment status={status} className="inline-flex items-center" />
        </button>
        <button
          type="button"
          onClick={v.stop}
          disabled={status === 'idle' || status === 'error'}
          aria-label="Stop (interrupt Aura)"
          className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-medium text-[#c9ccd6] transition-colors duration-fast hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Stop
        </button>
        {v.canReplay ? (
          <button
            type="button"
            onClick={async () => { if (replaying) return; setReplaying(true); try { await v.replayLast(); } finally { setReplaying(false); } }}
            disabled={replaying}
            aria-busy={replaying}
            aria-label="Replay Aura last spoken response"
            title="Replay Aura last response"
            className={cn(
              'inline-flex shrink-0 items-center gap-1.5 rounded-xl border px-4 py-3 text-sm font-medium transition-all duration-fast active:scale-[0.97]',
              replaying
                ? 'border-[#a9b0ff]/50 bg-[#a9b0ff]/15 text-[#c3c8ff] scale-[0.97]'
                : 'border-white/10 bg-white/5 text-[#c9ccd6] hover:bg-white/10',
            )}
          >
            <ReplayIcon className={replaying ? 'animate-spin' : undefined} />
            <span className="whitespace-nowrap">{replaying ? 'Replaying…' : 'Replay'}</span>
          </button>
        ) : null}
      </div>
      <p className="text-center text-[11px] text-[#7c828f]">
        Hold the button (or hold Space) to talk · release to send
      </p>

      {/* Daily briefing — sectioned on screen + paced spoken narration */}
      <button
        type="button"
        onClick={() => void v.briefMe()}
        disabled={v.briefingLoading || status === 'thinking'}
        aria-label="Hear today's briefing"
        className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm font-medium text-[#c9ccd6] transition-colors duration-fast hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {v.briefingLoading ? 'Preparing your briefing…' : "Hear today’s briefing"}
      </button>

      {/* Type-instead fallback */}
      <form
        className="flex gap-2"
        onSubmit={(e) => { e.preventDefault(); void submitText(); }}
      >
        <input
          className="w-full rounded-xl border border-white/10 px-3.5 py-2.5 text-sm text-white outline-none transition-colors placeholder:text-[#7c828f] focus:border-[#a9b0ff]/50"
          style={{ background: FIELD_BG }}
          placeholder="Type instead…"
          value={textDraft}
          onChange={(e) => setTextDraft(e.target.value)}
          aria-label="Type a message to Aura"
        />
        <button
          type="submit"
          disabled={textDraft.trim() === '' || status === 'thinking'}
          className="rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm font-medium text-[#c9ccd6] transition-colors duration-fast hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Send
        </button>
      </form>

      {/* Approval confirm step — the ONLY gate to an external action */}
      {state.pendingApproval ? (
        <div className="rounded-xl border border-[#a9b0ff]/30 bg-[#a9b0ff]/5 p-3">
          <p className="text-sm text-white">
            <span className="font-semibold">{persona.displayName} suggests:</span> {state.pendingApproval.summary}
          </p>
          <p className="mt-1 text-xs text-[#9aa0ad]">Nothing runs until you approve.</p>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={v.approve}
              className="rounded-lg px-3 py-1.5 text-sm font-semibold text-[#1a1726]"
              style={{ background: LAVENDER }}
            >
              Approve
            </button>
            <button
              type="button"
              onClick={v.reject}
              className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-sm font-medium text-[#c9ccd6] hover:bg-white/10"
            >
              Not now
            </button>
          </div>
        </div>
      ) : null}

      {/* Local demo (mock STT only) — feed a spoken phrase without a mic */}
      {demo ? (
        <form
          className="flex gap-2 border-t border-white/5 pt-3"
          onSubmit={(e) => {
            e.preventDefault();
            const t = demoDraft.trim();
            if (t === '') return;
            setDemoDraft('');
            void v.pressToTalk().then(() => { v.demoSay(t); void v.releaseToTalk(); });
          }}
        >
          <input
            className="w-full rounded-xl border border-dashed border-white/10 px-3.5 py-2.5 text-xs text-[#9aa0ad] outline-none"
            style={{ background: FIELD_BG }}
            placeholder="Demo: simulate a spoken phrase (mock STT)…"
            value={demoDraft}
            onChange={(e) => setDemoDraft(e.target.value)}
            aria-label="Demo spoken phrase"
          />
          <button type="submit" className="rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-[#c9ccd6] hover:bg-white/10">
            Say
          </button>
        </form>
      ) : null}
    </div>
  );
}
